<script setup lang="ts">
/**
 * Read-only diagnostic renderer for a `tool`-type part, composed from the
 * ported AI-Elements `Tool` family (header with status badge + collapsible
 * input/output). Used directly for display/editor tools and as the fallback
 * for interactive building blocks reloaded without their original args
 * (see `block-part.vue`).
 *
 * Tool input/output are shown as formatted JSON via the lightweight
 * `ToolInput`/`ToolOutput` (plain `<pre>`), an intentional fork of the
 * reference's shiki-backed code block kept out of the diagnostic path.
 */
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import type { ToolUiPart } from "../../types/chat";
import { Tool, ToolContent, ToolHeader, ToolInput, ToolOutput } from "../ai-elements/tool";

const props = defineProps<{
  tool: ToolUiPart;
  /** Fallback text (e.g. an error message) when the tool carries no structured output. */
  text?: string;
}>();

const { t } = useI18n({ useScope: "local" });

const displayName = computed(() => props.tool.name || t("fallbackName"));

const isError = computed(() => props.tool.state === "output-error");

const hasInput = computed(
  () => props.tool.input != null && Object.keys(props.tool.input ?? {}).length > 0,
);

/** For errors, prefer the part's error text; otherwise show structured output. */
const errorText = computed(() =>
  isError.value ? props.text || stringify(props.tool.output) : null,
);
const output = computed(() => (isError.value ? null : props.tool.output));

function stringify(value: unknown): string {
  if (value == null) {
    return "";
  }
  if (typeof value === "string") {
    return value;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}
</script>

<template>
  <Tool data-slot="chat-tool-part">
    <ToolHeader :title="displayName" :state="tool.state" />
    <ToolContent>
      <ToolInput v-if="hasInput" :input="tool.input" />
      <ToolOutput :output="output" :error-text="errorText" />
    </ToolContent>
  </Tool>
</template>

<i18n lang="yaml">
en:
  fallbackName: Tool
</i18n>
